import React, { useRef, useState } from 'react';
import css from './css/StudentSurveyBuildStep.scss';
import commonCss from './css/SurveyStyle.scss';
import classnames from 'classnames';
import AddStudentSurveyElementModal from './AddStudentSurveyElementModal';
import BuildSurveyElementsContainer from './BuildSurveyElementsContainer';
import StyledButton from 'common/StyledButton';
import Icon from 'SF/MaterialIcon';
import { getWord } from 'utils/tenant';
import { StudentSurveyElement } from 'utils/types';
import { mergeWith, isArray } from 'lodash';
import PropTypes from 'prop-types';

function StudentSurveyBuildStep({title, surveyElements, editable, setProperties}) {
    const studentSurveyCustomLanguage = getWord('studentSurvey', false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [elementType, setElementType] = useState('');
    const [elementToEdit, setElementToEdit] = useState(null);
    const addQuestionRef = useRef();

    const addElement = (type) => {
        setElementType(type);
        setIsModalOpen(true);
    };

    const replaceElement = (currentElements, elementToReplace, newElementData) =>
        currentElements.map(element => (element === elementToReplace) ?
            mergeWith({},
                element,
                newElementData,
                // In the case of an array, we want to replace the whole array not individual items
                (_, newValue) => isArray(newValue) ? newValue : undefined// eslint-disable-line no-undefined
            )
        : element)

    const findLargestId = (ids) => Math.max(...ids);

    const saveElement = (newElementData) => {
        const updatedElements = elementToEdit ? replaceElement(surveyElements, elementToEdit, newElementData)
            : [...surveyElements,
                {
                    id: surveyElements.length ? (findLargestId(surveyElements.map(elements => elements.id)) + 1) : 0,
                    elementType: elementType,
                    ...newElementData,
                    elementPosition: surveyElements.length ? surveyElements.length : 0
                }
            ];
        setProperties({elements: updatedElements});
    };

    const editElement = (newEditElement) => {
        setElementType(newEditElement.elementType);
        setElementToEdit(newEditElement);
        setIsModalOpen(true);
    };

    const deleteElement = (newDeleteElement) => {
        const remainderElements = surveyElements.filter(element => element !== newDeleteElement)
            .map((element, index) => (
                {...element, elementPosition: index}
            ));
        setProperties({elements: remainderElements});
        //For screen readers, focus on Add Question button after an element is deleted
        addQuestionRef.current && addQuestionRef.current.focus();
    };

    const reorderElements = (reorderedElements) =>
        setProperties({elements: reorderedElements.map((element, index) => ( {...element, elementPosition: index} ))});

    return (
        <>
            <h2 className={commonCss['header-second-level']}>Build</h2>
            <div className={classnames(commonCss['instructional-text'], commonCss['spacing-below'])}>
                This page cannot be modified after this {studentSurveyCustomLanguage} is scheduled.
            </div>
            {editable &&
                <div className={classnames(css['add-element-btns'], commonCss['spacing-below'])}>
                    <StyledButton ref={addQuestionRef} type='button' inverted className={commonCss['styled-button']}
                                  onClick={() => addElement('QUESTION')}>
                        <><Icon icon='add_circle' aria-hidden/> Question</>
                    </StyledButton>
                    <StyledButton type='button' inverted className={commonCss['styled-button']}
                                  onClick={() => addElement('HEADER')}>
                        <><Icon icon='add_circle' aria-hidden/> Section Header</>
                    </StyledButton>
                    <StyledButton type='button' inverted className={commonCss['styled-button']}
                                  onClick={() => addElement('INSTRUCTION')}>
                        <><Icon icon='add_circle' aria-hidden/> Text</>
                    </StyledButton>
                </div>
            }
            <BuildSurveyElementsContainer title={title}
                                          surveyElements={surveyElements}
                                          editable={editable}
                                          onEdit={(element) => editElement(element)}
                                          onDelete={(element) => deleteElement(element)}
                                          onReorder={(elementList) => reorderElements(elementList)} />
            <AddStudentSurveyElementModal isOpen={isModalOpen}
                                          closeModal={() => {setIsModalOpen(false); setElementToEdit(null);}}
                                          currentQuestionIds={surveyElements.filter(element => element.elementType === 'QUESTION')
                                              .map(question => question.templateQuestion.questionId)}
                                          elementType={elementType}
                                          onSave={(elementData) => saveElement(elementData)}
                                          elementDataToEdit={elementToEdit ? {
                                              elementText: elementToEdit.elementText,
                                              templateQuestion: elementToEdit.templateQuestion
                                          } : null} />
        </>
    )
}

StudentSurveyBuildStep.propTypes = {
    title: PropTypes.string.isRequired,
    surveyElements: PropTypes.arrayOf(StudentSurveyElement).isRequired,
    editable: PropTypes.bool.isRequired,
    setProperties: PropTypes.func.isRequired
};

export default StudentSurveyBuildStep;
